// Transcripts: the latest transcription of a media file, its segments and
// word timings, edits, and the AI tools that run on it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cutAt, normalizeCuts, type Cut } from '@podcastwiz-app/shared';
import { api } from './api';

export type TranscriptStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface Transcription {
  id: string;
  mediaFileId: string;
  status: TranscriptStatus;
  language: string | null;
  wordCount: number | null;
  createdAt: string;
}

export interface Segment {
  id: string;
  speakerId: string | null;
  speakerName: string | null;
  startMs: number;
  endMs: number;
  text: string;
}

export interface Word {
  id: string;
  text: string;
  startMs: number;
  endMs: number;
  wordIndex: number;
}

/** A segment with the words spoken inside it. */
export interface Paragraph extends Segment {
  words: Word[];
  /** The text was corrected by hand, so the word timings no longer match it. */
  edited: boolean;
}

const inFlight = (s?: TranscriptStatus) => s === 'pending' || s === 'processing';

/** The newest transcription of a media file, if any. */
export function useLatestTranscription(mediaFileId: string | null) {
  return useQuery<Transcription | null>({
    queryKey: ['transcriptions', mediaFileId],
    enabled: !!mediaFileId,
    queryFn: async () =>
      (await api<{ transcriptions: Transcription[] }>(`/api/transcriptions?mediaFileId=${mediaFileId}`))
        .transcriptions[0] ?? null,
    refetchInterval: (q) => (inFlight(q.state.data?.status) ? 3000 : false),
  });
}

export function useTranscript(id: string | undefined) {
  return useQuery<{ transcription: Transcription; segments: Segment[] }>({
    queryKey: ['transcript', id],
    enabled: !!id,
    queryFn: () => api(`/api/transcriptions/${id}`),
  });
}

export function useWords(id: string | undefined) {
  return useQuery<Word[]>({
    queryKey: ['transcript-words', id],
    enabled: !!id,
    queryFn: async () => (await api<{ words: Word[] }>(`/api/transcriptions/${id}/words`)).words,
    staleTime: Infinity,
  });
}

export function useStartTranscription(mediaFileId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api('/api/transcriptions', {
        method: 'POST',
        body: JSON.stringify({ mediaFileId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcriptions', mediaFileId] }),
  });
}

export function useUpdateSegment(transcriptionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, text }: { segmentId: string; text: string }) =>
      api(`/api/transcriptions/${transcriptionId}/segments/${segmentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcript', transcriptionId] }),
  });
}

export function useRenameSpeaker(transcriptionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ speakerId, name }: { speakerId: string; name: string }) =>
      api(`/api/transcriptions/${transcriptionId}/speakers/${encodeURIComponent(speakerId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcript', transcriptionId] }),
  });
}

const words = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

/**
 * Attach each word to the segment it was spoken in (by time: words don't
 * carry a segment id). Both lists are sorted by start time.
 */
export function toParagraphs(segments: Segment[], allWords: Word[]): Paragraph[] {
  let w = 0;
  return segments.map((seg, i) => {
    const next = segments[i + 1];
    const own: Word[] = [];
    // Up to the next segment's start; words before the first segment go to it.
    while (w < allWords.length && (!next || allWords[w]!.startMs < next.startMs)) {
      own.push(allWords[w]!);
      w++;
    }
    const edited = own.length > 0 && words(own.map((x) => x.text).join(' ')).join(' ') !== words(seg.text).join(' ');
    return { ...seg, words: own, edited };
  });
}

/** Index of the word being spoken at `ms`, or -1. */
export function activeWordIndex(ws: Word[], ms: number): number {
  let lo = 0;
  let hi = ws.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ws[mid]!.startMs <= ms) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return found >= 0 && ms <= ws[found]!.endMs + 250 ? found : -1;
}

/** "Speaker 1" by first appearance, unless the user named them. */
export function speakerNames(segments: Segment[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const s of segments) {
    if (s.speakerId === null || names.has(s.speakerId)) continue;
    names.set(s.speakerId, s.speakerName || `Speaker ${names.size + 1}`);
  }
  return names;
}

export function clock(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

// ─── AI tools ────────────────────────────────────────────────────────

export interface AiResult {
  jobType: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  errorMessage: string | null;
  outputData: Record<string, unknown> | null;
  updatedAt: string;
}

export interface AiTool {
  /** processing_jobs.jobType */
  type: string;
  /** POST /api/transcriptions/:id/<route> */
  route: string;
  label: string;
  blurb: string;
}

export const AI_TOOLS: AiTool[] = [
  {
    type: 'show-notes',
    route: 'show-notes',
    label: 'Show notes',
    blurb: 'A summary and key points, saved on the episode.',
  },
  {
    type: 'chapters',
    route: 'chapters',
    label: 'Chapters',
    blurb: 'Titled chapters with start times.',
  },
  {
    type: 'title-suggest',
    route: 'titles',
    label: 'Titles',
    blurb: 'Five title ideas.',
  },
  {
    type: 'social-posts',
    route: 'social-posts',
    label: 'Social posts',
    blurb: 'A promo post for each platform.',
  },
  {
    type: 'highlight-detect',
    route: 'highlights',
    label: 'Highlights',
    blurb: 'The moments worth clipping.',
  },
  {
    type: 'hooks',
    route: 'hooks',
    label: 'Hooks',
    blurb: 'Quotable cold-open lines.',
  },
];

export function useAiResults(transcriptionId: string | undefined) {
  return useQuery<Record<string, AiResult>>({
    queryKey: ['ai-results', transcriptionId],
    enabled: !!transcriptionId,
    queryFn: async () =>
      (await api<{ results: Record<string, AiResult> }>(`/api/transcriptions/${transcriptionId}/ai-results`)).results,
    refetchInterval: (q) =>
      Object.values(q.state.data ?? {}).some((r) => r.status === 'pending' || r.status === 'active') ? 2500 : false,
  });
}

export function useRunTool(transcriptionId: string, episodeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tool: AiTool) =>
      api(`/api/transcriptions/${transcriptionId}/${tool.route}`, {
        method: 'POST',
        body: JSON.stringify({ episodeId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-results', transcriptionId] });
      void qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/** Save a transcript export (fetched with the session token, then saved as a file). */
export async function downloadTranscript(
  id: string,
  format: 'srt' | 'vtt' | 'txt',
  fetchText = (p: string) => api<string>(p),
) {
  const text = await fetchText(`/api/transcriptions/${id}/export?format=${format}`);
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `transcript.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Edits ───────────────────────────────────────────────────────────

/** Save transcript cuts and start rendering them (empty = revert). */
export function useSaveCuts(mediaFileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuts: Cut[]) =>
      api<{ cuts: Cut[]; jobId: string }>(`/api/media/${mediaFileId}/cuts`, {
        method: 'PUT',
        body: JSON.stringify({ cuts }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', mediaFileId] }),
  });
}

export interface JobState {
  id: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  progress: number;
  errorMessage: string | null;
}

/** Poll one job until it finishes. */
export function useJob(jobId: string | null) {
  return useQuery<JobState>({
    queryKey: ['job', jobId],
    enabled: !!jobId,
    queryFn: async () => (await api<{ job: JobState }>(`/api/jobs/${jobId}`)).job,
    refetchInterval: (q) => (q.state.data?.status === 'completed' || q.state.data?.status === 'failed' ? false : 2000),
  });
}

/** Cuts covering exactly the given words (first start to last end). */
export function cutForWords(ws: Word[]): Cut | null {
  if (ws.length === 0) return null;
  return { startMs: ws[0]!.startMs, endMs: ws[ws.length - 1]!.endMs };
}

/** A word counts as cut when its midpoint is inside a cut. */
export function isWordCut(w: Word, cuts: Cut[]): boolean {
  return !!cutAt((w.startMs + w.endMs) / 2, cuts);
}

export const sameCuts = (a: Cut[], b: Cut[]) => JSON.stringify(normalizeCuts(a)) === JSON.stringify(normalizeCuts(b));
